/**
 * PermissionRoute — granular permission-based route guard (Phase 3b).
 *
 * Replaces the old module/level-based RoleRoute with key-based checks.
 *
 * @example
 * ```tsx
 * // Require a single permission key
 * <PermissionRoute permission={PK.Applications.View}>
 *   <ApplicationsPage />
 * </PermissionRoute>
 *
 * // Require any of several keys (OR)
 * <PermissionRoute anyOf={[PK.Expenses.AllView, PK.Expenses.OwnView]}>
 *   <ExpensesPage />
 * </PermissionRoute>
 *
 * // Tab-based (module) visibility
 * <PermissionRoute module="applications">
 *   <ApplicationsPage />
 * </PermissionRoute>
 * ```
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissionStore } from '@/store/permissionStore';
import { useIsAuthenticated, useAuthStore, selectIsInitializing, selectIsAuthPermissionsLoaded } from '@/store';
import { TAB_PERMISSION } from '@/constants/permissions';
import { ROUTES } from '@/constants';
import { PageLoading } from '@/components/ui';
import { ProtectedRoute } from './ProtectedRoute';

// ============================================================================
// Types
// ============================================================================

export interface PermissionRouteProps {
  children: ReactNode;
  /** Require this specific permission key */
  permission?: string;
  /** Require the tab-visibility key for this module name */
  module?: string;
  /** Require any of these keys (OR logic) */
  anyOf?: string[];
  /** Where to redirect if access is denied (default: /forbidden) */
  fallbackTo?: string;
}

// ============================================================================
// Component
// ============================================================================

export function PermissionRoute({
  children,
  permission,
  module,
  anyOf,
  fallbackTo = ROUTES.errors?.forbidden ?? '/forbidden',
}: PermissionRouteProps): JSX.Element {
  const isAuthenticated = useIsAuthenticated();
  const isInitializing = useAuthStore(selectIsInitializing);
  const isPermissionsLoaded = useAuthStore(selectIsAuthPermissionsLoaded);
  const keys = usePermissionStore((s) => s.keys);
  const isPermissionsLoading = usePermissionStore((s) => s.isLoading);
  const location = useLocation();

  if (isInitializing) {
    return <PageLoading />;
  }

  if (!isAuthenticated) {
    return <ProtectedRoute>{children}</ProtectedRoute>;
  }

  if (!isPermissionsLoaded) {
    return <PageLoading />;
  }

  // Hold the route while a refresh is in flight (e.g. just after saving the
  // permissions matrix) to avoid bouncing to /403 between fetches.
  if (isPermissionsLoading) {
    return <PageLoading />;
  }

  // Determine if access is granted
  let accessGranted = false;

  if (!permission && !module && (!anyOf || anyOf.length === 0)) {
    // No restrictions — act like ProtectedRoute
    accessGranted = true;
  } else if (keys) {
    if (permission) {
      accessGranted = keys.has(permission);
    }
    if (!accessGranted && module) {
      const tabKey = TAB_PERMISSION[module];
      accessGranted = tabKey ? keys.has(tabKey) : false;
    }
    if (!accessGranted && anyOf && anyOf.length > 0) {
      accessGranted = anyOf.some((k) => keys.has(k));
    }
  }

  if (!accessGranted) {
    return (
      <Navigate
        to={fallbackTo}
        state={{ from: location, accessDenied: true }}
        replace
      />
    );
  }

  return <>{children}</>;
}

export default PermissionRoute;
